// Canonical ISO 3166-1 alpha-2 country list (lowercase codes) + the mapping
// from a country to its default pricing currency. Currencies are constrained to
// the platform's configured display currencies (those with FX rates) — a listing
// priced in an unconfigured currency would have no rate and break booking math,
// so unmapped countries return null and the operator picks a configured currency.

/** The display currencies configured with FX rates (currency-settings). */
export const CONFIGURED_CURRENCIES = [
  // Original DriveCars markets.
  'EUR', 'USD', 'GBP', 'MAD', 'AED',
  // Added for the global Avis supplier destinations — the scraped price is
  // stored native and converted to EUR at read time via currency-settings
  // rateToPrimary (see scripts/db-split/0128 which seeds the rates + pairRates).
  'QAR', 'OMR', 'IDR', 'JPY', 'THB', 'SGD', 'MYR', 'KRW', 'VND', 'PHP', 'INR',
  'ZAR', 'KES', 'TZS', 'NAD', 'ISK', 'CHF', 'CZK', 'CAD', 'MXN', 'BRL', 'ARS',
  'PEN', 'AUD', 'NZD', 'CLP',
  // Full Avis catalog (all ~170 countries) — seeded in scripts/db-split/0129.
  'ALL', 'AMD', 'ANG', 'AOA', 'AWG', 'AZN', 'BAM', 'BBD', 'BGN', 'BHD', 'BND', 'BOB',
  'BSD', 'BWP', 'BZD', 'CDF', 'CNY', 'CRC', 'DKK', 'DOP', 'EGP', 'FJD', 'GEL', 'GHS',
  'GMD', 'GNF', 'GTQ', 'HKD', 'HNL', 'HTG', 'HUF', 'ILS', 'IQD', 'JMD', 'JOD', 'KHR',
  'KWD', 'KYD', 'KZT', 'LAK', 'LBP', 'LSL', 'LYD', 'MDL', 'MGA', 'MKD', 'MOP', 'MUR',
  'MWK', 'MZN', 'NGN', 'NIO', 'NOK', 'PAB', 'PGK', 'PKR', 'PLN', 'RON', 'RSD', 'SAR',
  'SBD', 'SCR', 'SEK', 'SRD', 'STN', 'SZL', 'TND', 'TOP', 'TRY', 'TTD', 'TWD', 'UAH',
  'UYU', 'VES', 'VUV', 'WST', 'XAF', 'XCD', 'XOF', 'XPF', 'ZMW',
] as const;
export type ConfiguredCurrency = (typeof CONFIGURED_CURRENCIES)[number];

export interface Country {
  /** ISO 3166-1 alpha-2, lowercase. */
  code: string;
  /** English short name. */
  name: string;
}

// Full ISO 3166-1 alpha-2 list (sovereign states + commonly-used territories).
export const COUNTRIES: ReadonlyArray<Country> = [
  { code: 'af', name: 'Afghanistan' }, { code: 'ax', name: 'Åland Islands' },
  { code: 'al', name: 'Albania' }, { code: 'dz', name: 'Algeria' },
  { code: 'as', name: 'American Samoa' }, { code: 'ad', name: 'Andorra' },
  { code: 'ao', name: 'Angola' }, { code: 'ai', name: 'Anguilla' },
  { code: 'aq', name: 'Antarctica' }, { code: 'ag', name: 'Antigua and Barbuda' },
  { code: 'ar', name: 'Argentina' }, { code: 'am', name: 'Armenia' },
  { code: 'aw', name: 'Aruba' }, { code: 'au', name: 'Australia' },
  { code: 'at', name: 'Austria' }, { code: 'az', name: 'Azerbaijan' },
  { code: 'bs', name: 'Bahamas' }, { code: 'bh', name: 'Bahrain' },
  { code: 'bd', name: 'Bangladesh' }, { code: 'bb', name: 'Barbados' },
  { code: 'by', name: 'Belarus' }, { code: 'be', name: 'Belgium' },
  { code: 'bz', name: 'Belize' }, { code: 'bj', name: 'Benin' },
  { code: 'bm', name: 'Bermuda' }, { code: 'bt', name: 'Bhutan' },
  { code: 'bo', name: 'Bolivia' }, { code: 'bq', name: 'Bonaire, Sint Eustatius and Saba' },
  { code: 'ba', name: 'Bosnia and Herzegovina' }, { code: 'bw', name: 'Botswana' },
  { code: 'bv', name: 'Bouvet Island' }, { code: 'br', name: 'Brazil' },
  { code: 'io', name: 'British Indian Ocean Territory' }, { code: 'bn', name: 'Brunei Darussalam' },
  { code: 'bg', name: 'Bulgaria' }, { code: 'bf', name: 'Burkina Faso' },
  { code: 'bi', name: 'Burundi' }, { code: 'cv', name: 'Cabo Verde' },
  { code: 'kh', name: 'Cambodia' }, { code: 'cm', name: 'Cameroon' },
  { code: 'ca', name: 'Canada' }, { code: 'ky', name: 'Cayman Islands' },
  { code: 'cf', name: 'Central African Republic' }, { code: 'td', name: 'Chad' },
  { code: 'cl', name: 'Chile' }, { code: 'cn', name: 'China' },
  { code: 'cx', name: 'Christmas Island' }, { code: 'cc', name: 'Cocos (Keeling) Islands' },
  { code: 'co', name: 'Colombia' }, { code: 'km', name: 'Comoros' },
  { code: 'cg', name: 'Congo' }, { code: 'cd', name: 'Congo (Democratic Republic)' },
  { code: 'ck', name: 'Cook Islands' }, { code: 'cr', name: 'Costa Rica' },
  { code: 'ci', name: "Côte d'Ivoire" }, { code: 'hr', name: 'Croatia' },
  { code: 'cu', name: 'Cuba' }, { code: 'cw', name: 'Curaçao' },
  { code: 'cy', name: 'Cyprus' }, { code: 'cz', name: 'Czechia' },
  { code: 'dk', name: 'Denmark' }, { code: 'dj', name: 'Djibouti' },
  { code: 'dm', name: 'Dominica' }, { code: 'do', name: 'Dominican Republic' },
  { code: 'ec', name: 'Ecuador' }, { code: 'eg', name: 'Egypt' },
  { code: 'sv', name: 'El Salvador' }, { code: 'gq', name: 'Equatorial Guinea' },
  { code: 'er', name: 'Eritrea' }, { code: 'ee', name: 'Estonia' },
  { code: 'sz', name: 'Eswatini' }, { code: 'et', name: 'Ethiopia' },
  { code: 'fk', name: 'Falkland Islands' }, { code: 'fo', name: 'Faroe Islands' },
  { code: 'fj', name: 'Fiji' }, { code: 'fi', name: 'Finland' },
  { code: 'fr', name: 'France' }, { code: 'gf', name: 'French Guiana' },
  { code: 'pf', name: 'French Polynesia' }, { code: 'tf', name: 'French Southern Territories' },
  { code: 'ga', name: 'Gabon' }, { code: 'gm', name: 'Gambia' },
  { code: 'ge', name: 'Georgia' }, { code: 'de', name: 'Germany' },
  { code: 'gh', name: 'Ghana' }, { code: 'gi', name: 'Gibraltar' },
  { code: 'gr', name: 'Greece' }, { code: 'gl', name: 'Greenland' },
  { code: 'gd', name: 'Grenada' }, { code: 'gp', name: 'Guadeloupe' },
  { code: 'gu', name: 'Guam' }, { code: 'gt', name: 'Guatemala' },
  { code: 'gg', name: 'Guernsey' }, { code: 'gn', name: 'Guinea' },
  { code: 'gw', name: 'Guinea-Bissau' }, { code: 'gy', name: 'Guyana' },
  { code: 'ht', name: 'Haiti' }, { code: 'hm', name: 'Heard Island and McDonald Islands' },
  { code: 'va', name: 'Holy See' }, { code: 'hn', name: 'Honduras' },
  { code: 'hk', name: 'Hong Kong' }, { code: 'hu', name: 'Hungary' },
  { code: 'is', name: 'Iceland' }, { code: 'in', name: 'India' },
  { code: 'id', name: 'Indonesia' }, { code: 'ir', name: 'Iran' },
  { code: 'iq', name: 'Iraq' }, { code: 'ie', name: 'Ireland' },
  { code: 'im', name: 'Isle of Man' }, { code: 'il', name: 'Israel' },
  { code: 'it', name: 'Italy' }, { code: 'jm', name: 'Jamaica' },
  { code: 'jp', name: 'Japan' }, { code: 'je', name: 'Jersey' },
  { code: 'jo', name: 'Jordan' }, { code: 'kz', name: 'Kazakhstan' },
  { code: 'ke', name: 'Kenya' }, { code: 'ki', name: 'Kiribati' },
  { code: 'kp', name: 'North Korea' }, { code: 'kr', name: 'South Korea' },
  { code: 'xk', name: 'Kosovo' }, // user-assigned code (not in ISO 3166-1); Avis lists it as KS0
  { code: 'kw', name: 'Kuwait' }, { code: 'kg', name: 'Kyrgyzstan' },
  { code: 'la', name: 'Laos' }, { code: 'lv', name: 'Latvia' },
  { code: 'lb', name: 'Lebanon' }, { code: 'ls', name: 'Lesotho' },
  { code: 'lr', name: 'Liberia' }, { code: 'ly', name: 'Libya' },
  { code: 'li', name: 'Liechtenstein' }, { code: 'lt', name: 'Lithuania' },
  { code: 'lu', name: 'Luxembourg' }, { code: 'mo', name: 'Macao' },
  { code: 'mg', name: 'Madagascar' }, { code: 'mw', name: 'Malawi' },
  { code: 'my', name: 'Malaysia' }, { code: 'mv', name: 'Maldives' },
  { code: 'ml', name: 'Mali' }, { code: 'mt', name: 'Malta' },
  { code: 'mh', name: 'Marshall Islands' }, { code: 'mq', name: 'Martinique' },
  { code: 'mr', name: 'Mauritania' }, { code: 'mu', name: 'Mauritius' },
  { code: 'yt', name: 'Mayotte' }, { code: 'mx', name: 'Mexico' },
  { code: 'fm', name: 'Micronesia' }, { code: 'md', name: 'Moldova' },
  { code: 'mc', name: 'Monaco' }, { code: 'mn', name: 'Mongolia' },
  { code: 'me', name: 'Montenegro' }, { code: 'ms', name: 'Montserrat' },
  { code: 'ma', name: 'Morocco' }, { code: 'mz', name: 'Mozambique' },
  { code: 'mm', name: 'Myanmar' }, { code: 'na', name: 'Namibia' },
  { code: 'nr', name: 'Nauru' }, { code: 'np', name: 'Nepal' },
  { code: 'nl', name: 'Netherlands' }, { code: 'nc', name: 'New Caledonia' },
  { code: 'nz', name: 'New Zealand' }, { code: 'ni', name: 'Nicaragua' },
  { code: 'ne', name: 'Niger' }, { code: 'ng', name: 'Nigeria' },
  { code: 'nu', name: 'Niue' }, { code: 'nf', name: 'Norfolk Island' },
  { code: 'mk', name: 'North Macedonia' }, { code: 'mp', name: 'Northern Mariana Islands' },
  { code: 'no', name: 'Norway' }, { code: 'om', name: 'Oman' },
  { code: 'pk', name: 'Pakistan' }, { code: 'pw', name: 'Palau' },
  { code: 'ps', name: 'Palestine' }, { code: 'pa', name: 'Panama' },
  { code: 'pg', name: 'Papua New Guinea' }, { code: 'py', name: 'Paraguay' },
  { code: 'pe', name: 'Peru' }, { code: 'ph', name: 'Philippines' },
  { code: 'pn', name: 'Pitcairn' }, { code: 'pl', name: 'Poland' },
  { code: 'pt', name: 'Portugal' }, { code: 'pr', name: 'Puerto Rico' },
  { code: 'qa', name: 'Qatar' }, { code: 're', name: 'Réunion' },
  { code: 'ro', name: 'Romania' }, { code: 'ru', name: 'Russia' },
  { code: 'rw', name: 'Rwanda' }, { code: 'bl', name: 'Saint Barthélemy' },
  { code: 'sh', name: 'Saint Helena' }, { code: 'kn', name: 'Saint Kitts and Nevis' },
  { code: 'lc', name: 'Saint Lucia' }, { code: 'mf', name: 'Saint Martin' },
  { code: 'pm', name: 'Saint Pierre and Miquelon' }, { code: 'vc', name: 'Saint Vincent and the Grenadines' },
  { code: 'ws', name: 'Samoa' }, { code: 'sm', name: 'San Marino' },
  { code: 'st', name: 'Sao Tome and Principe' }, { code: 'sa', name: 'Saudi Arabia' },
  { code: 'sn', name: 'Senegal' }, { code: 'rs', name: 'Serbia' },
  { code: 'sc', name: 'Seychelles' }, { code: 'sl', name: 'Sierra Leone' },
  { code: 'sg', name: 'Singapore' }, { code: 'sx', name: 'Sint Maarten' },
  { code: 'sk', name: 'Slovakia' }, { code: 'si', name: 'Slovenia' },
  { code: 'sb', name: 'Solomon Islands' }, { code: 'so', name: 'Somalia' },
  { code: 'za', name: 'South Africa' }, { code: 'gs', name: 'South Georgia' },
  { code: 'ss', name: 'South Sudan' }, { code: 'es', name: 'Spain' },
  { code: 'lk', name: 'Sri Lanka' }, { code: 'sd', name: 'Sudan' },
  { code: 'sr', name: 'Suriname' }, { code: 'sj', name: 'Svalbard and Jan Mayen' },
  { code: 'se', name: 'Sweden' }, { code: 'ch', name: 'Switzerland' },
  { code: 'sy', name: 'Syria' }, { code: 'tw', name: 'Taiwan' },
  { code: 'tj', name: 'Tajikistan' }, { code: 'tz', name: 'Tanzania' },
  { code: 'th', name: 'Thailand' }, { code: 'tl', name: 'Timor-Leste' },
  { code: 'tg', name: 'Togo' }, { code: 'tk', name: 'Tokelau' },
  { code: 'to', name: 'Tonga' }, { code: 'tt', name: 'Trinidad and Tobago' },
  { code: 'tn', name: 'Tunisia' }, { code: 'tr', name: 'Türkiye' },
  { code: 'tm', name: 'Turkmenistan' }, { code: 'tc', name: 'Turks and Caicos Islands' },
  { code: 'tv', name: 'Tuvalu' }, { code: 'ug', name: 'Uganda' },
  { code: 'ua', name: 'Ukraine' }, { code: 'ae', name: 'United Arab Emirates' },
  { code: 'gb', name: 'United Kingdom' }, { code: 'us', name: 'United States' },
  { code: 'um', name: 'United States Minor Outlying Islands' }, { code: 'uy', name: 'Uruguay' },
  { code: 'uz', name: 'Uzbekistan' }, { code: 'vu', name: 'Vanuatu' },
  { code: 've', name: 'Venezuela' }, { code: 'vn', name: 'Vietnam' },
  { code: 'vg', name: 'Virgin Islands (British)' }, { code: 'vi', name: 'Virgin Islands (U.S.)' },
  { code: 'wf', name: 'Wallis and Futuna' }, { code: 'eh', name: 'Western Sahara' },
  { code: 'ye', name: 'Yemen' }, { code: 'zm', name: 'Zambia' },
  { code: 'zw', name: 'Zimbabwe' },
];

// Eurozone members → EUR (their FX rate is configured).
const EUROZONE = [
  'at', 'be', 'cy', 'de', 'ee', 'es', 'fi', 'fr', 'gr', 'hr', 'ie', 'it',
  'lt', 'lu', 'lv', 'mt', 'nl', 'pt', 'si', 'sk',
];

/** country code → default configured currency, or undefined when unmapped. */
export const COUNTRY_CURRENCY: Readonly<Record<string, ConfiguredCurrency>> = {
  ma: 'MAD',
  ae: 'AED',
  gb: 'GBP',
  us: 'USD',
  // Avis destination markets (ISO-2 → native currency).
  qa: 'QAR', om: 'OMR', id: 'IDR', jp: 'JPY', th: 'THB', sg: 'SGD', my: 'MYR',
  kr: 'KRW', vn: 'VND', ph: 'PHP', in: 'INR', za: 'ZAR', ke: 'KES', tz: 'TZS',
  na: 'NAD', is: 'ISK', ch: 'CHF', cz: 'CZK', ca: 'CAD', mx: 'MXN', br: 'BRL',
  ar: 'ARS', pe: 'PEN', au: 'AUD', nz: 'NZD', cl: 'CLP',
  // Zimbabwe (Victoria Falls) prices in USD.
  zw: 'USD',
  // Full Avis catalog — every remaining served country (ISO-2 -> native currency).
  ag: 'XCD', ai: 'XCD', al: 'ALL', am: 'AMD', ao: 'AOA', aw: 'AWG', az: 'AZN',
  ba: 'BAM', bb: 'BBD', bf: 'XOF', bg: 'BGN', bh: 'BHD', bj: 'XOF', bl: 'EUR',
  bn: 'BND', bo: 'BOB', bq: 'USD', bs: 'BSD', bw: 'BWP', bz: 'BZD', cd: 'CDF',
  cf: 'XAF', cg: 'XAF', ci: 'XOF', ck: 'NZD', cm: 'XAF', cn: 'CNY', cr: 'CRC',
  cw: 'ANG', dk: 'DKK', dm: 'XCD', do: 'DOP', ec: 'USD', eg: 'EGP', fj: 'FJD',
  fo: 'DKK', ga: 'XAF', gd: 'XCD', ge: 'GEL', gf: 'EUR', gh: 'GHS', gm: 'GMD',
  gn: 'GNF', gp: 'EUR', gq: 'XAF', gt: 'GTQ', gu: 'USD', hk: 'HKD', hn: 'HNL',
  ht: 'HTG', hu: 'HUF', il: 'ILS', iq: 'IQD', jm: 'JMD', jo: 'JOD', kh: 'KHR',
  kn: 'XCD', kw: 'KWD', ky: 'KYD', kz: 'KZT', la: 'LAK', lb: 'LBP', lc: 'XCD',
  ls: 'LSL', ly: 'LYD', md: 'MDL', me: 'EUR', mf: 'EUR', mg: 'MGA', mk: 'MKD',
  ml: 'XOF', mo: 'MOP', mq: 'EUR', mu: 'MUR', mw: 'MWK', mz: 'MZN', nc: 'XPF',
  ne: 'XOF', ng: 'NGN', ni: 'NIO', no: 'NOK', pa: 'PAB', pf: 'XPF', pg: 'PGK',
  pk: 'PKR', pl: 'PLN', pr: 'USD', re: 'EUR', ro: 'RON', rs: 'RSD', sa: 'SAR',
  sb: 'SBD', sc: 'SCR', se: 'SEK', sn: 'XOF', sr: 'SRD', st: 'STN', sv: 'USD',
  sx: 'ANG', sz: 'SZL', tc: 'USD', td: 'XAF', tg: 'XOF', tn: 'TND', to: 'TOP',
  tr: 'TRY', tt: 'TTD', tw: 'TWD', ua: 'UAH', uy: 'UYU', vc: 'XCD', ve: 'VES',
  vg: 'USD', vi: 'USD', vu: 'VUV', ws: 'WST', xk: 'EUR', yt: 'EUR', zm: 'ZMW',
  ...Object.fromEntries(EUROZONE.map((c) => [c, 'EUR' as ConfiguredCurrency])),
};

/** The default configured pricing currency for a country, or null if none. */
export function currencyForCountry(code: string | null | undefined): ConfiguredCurrency | null {
  if (!code) return null;
  return COUNTRY_CURRENCY[code.toLowerCase()] ?? null;
}

// ── Location-slug humanizer ──────────────────────────────────────────────────
// Supplier-fed (Avis) location slugs are `<city>-<country>-<n>`, e.g.
// `birmingham-united-kingdom-2`, `baton-rouge-united-states-5`,
// `sault-sainte-marie-airport-canada`. Both city and country can be multi-word.
// The API returns a clean localized `name` for these rows, so this is only a
// LAST-RESORT fallback for the rare case where `name` is genuinely null — it
// must degrade to "Birmingham", never to the raw slug or "Birmingham United
// Kingdom 2". We strip a trailing numeric dedup segment, then a trailing
// country-name slug (longest match wins, so "united-kingdom" beats nothing),
// then title-case what remains.

/** Country-name slugs ("united-kingdom", "south-africa", "france", …), sorted
 *  longest-first so multi-word countries are matched before any single-word
 *  tail they might end with. Built once from COUNTRIES. */
const COUNTRY_NAME_SLUGS: readonly string[] = COUNTRIES
  .map((c) =>
    c.name
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '') // strip diacritics (Åland → aland)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, ''),
  )
  .filter(Boolean)
  .sort((a, b) => b.length - a.length);

/** Turn a location slug into a clean human label. Strips the Avis
 *  `-<country>-<n>` tail and title-cases the rest. Idempotent-ish on already
 *  clean slugs (`agadir` → "Agadir"). Two-letter tokens are upper-cased
 *  (state/airport codes like "WA", "JFK"). */
export function humanizeLocationSlug(slug: string | null | undefined): string {
  if (!slug) return '';
  let rest = slug.toLowerCase().trim();
  // 1. Trailing numeric dedup suffix (…-2, …-10).
  rest = rest.replace(/-\d+$/, '');
  // 2. Trailing country-name slug (longest match first).
  for (const country of COUNTRY_NAME_SLUGS) {
    if (rest === country) break; // slug is *only* the country — keep it as-is
    if (rest.endsWith(`-${country}`)) {
      rest = rest.slice(0, -(country.length + 1));
      break;
    }
  }
  // 3. Title-case the remaining city (+ any "airport"/"station" descriptor).
  const words = rest.split('-').filter(Boolean);
  if (words.length === 0) return '';
  return words
    .map((w) => (w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/** Resolve a display name for a location: prefer the localized `name`, else
 *  humanize the slug. Never returns the raw slug. */
export function locationDisplayName(
  name: string | null | undefined,
  slug: string | null | undefined,
): string {
  const trimmed = name?.trim();
  if (trimmed) return trimmed;
  return humanizeLocationSlug(slug);
}
